import React, { useState, useEffect } from 'react'
import { DatePicker, Button } from 'antd'
import moment from 'moment'
import api from '../../../../../../services/api'
import './HistoricalLeaderboard.scss'
import LeaderboardTable from '../../../LeaderboardTable'
import { useTranslation } from 'react-i18next'

const { RangePicker } = DatePicker

const initialDateRange = [
  moment()
    .subtract(1, 'month')
    .startOf('month'),
  moment()
    .subtract(1, 'month')
    .endOf('month')
]

const HistoricalLeaderboard = ({ selectedCompetition, userId }) => {
  const [selectedPreset, setSelectedPreset] = useState('lastMonth')
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [dateRange, setDateRange] = useState(initialDateRange)
  const [historicalData, setHistoricalData] = useState([])
  const [fetchingHistorical, setFetchingHistorical] = useState(false)
  const [error, setError] = useState(null)

  const { t } = useTranslation()

  // Initialize with last month if no date range provided
  useEffect(() => {
    if (!dateRange || dateRange.length === 0) {
      const lastMonth = [
        moment()
          .subtract(1, 'month')
          .startOf('month'),
        moment()
          .subtract(1, 'month')
          .endOf('month')
      ]
      setDateRange(lastMonth)
    }
  }, [dateRange])

  // Fetch historical data when component mounts or date range changes
  useEffect(() => {
    if (
      dateRange.length === 2 &&
      selectedCompetition &&
      selectedCompetition.value &&
      userId
    ) {
      getHistoricalData(dateRange[0], dateRange[1])
    }
  }, [dateRange, selectedCompetition, userId])

  const getHistoricalData = async (fromDate, toDate) => {
    try {
      setFetchingHistorical(true)
      setError(null)

      // Ensure we have a selected competition
      if (!selectedCompetition || !selectedCompetition.value) {
        setError('No competition selected. Please select a competition first.')
        return
      }

      const response = await api.leaderboard.getCompetitionUser({
        id: selectedCompetition.value,
        userId: userId,
        date: '',
        from: fromDate.format('YYYY-MM-DD'),
        to: toDate.format('YYYY-MM-DD')
      })
      if (response.data && Array.isArray(response.data.leaderboard)) {
        setHistoricalData(response.data.leaderboard)
      } else {
        setHistoricalData([])
      }
    } catch (error) {
      console.error('Error fetching historical data:', error)
      setError('Failed to load historical data. Please try again.')
    } finally {
      setFetchingHistorical(false)
    }
  }

  const handleDateRangeChange = dates => {
    if (dates) {
      setDateRange(dates)
      setSelectedPreset('custom')
    }
  }

  const handlePresetChange = preset => {
    setSelectedPreset(preset)
    const today = moment()
    let startDate, endDate

    switch (preset) {
      case 'today':
        startDate = today.startOf('day')
        endDate = today.endOf('day')
        break
      case 'yesterday':
        startDate = today
          .clone()
          .subtract(1, 'day')
          .startOf('day')
        endDate = today
          .clone()
          .subtract(1, 'day')
          .endOf('day')
        break
      case 'lastWeek':
        startDate = today
          .clone()
          .subtract(1, 'week')
          .startOf('week')
        endDate = today
          .clone()
          .subtract(1, 'week')
          .endOf('week')
        break
      case 'lastMonth':
        startDate = today
          .clone()
          .subtract(1, 'month')
          .startOf('month')
        endDate = today
          .clone()
          .subtract(1, 'month')
          .endOf('month')
        break
      case 'lastQuarter':
        startDate = today
          .clone()
          .subtract(3, 'month')
          .startOf('month')
        endDate = today
          .clone()
          .subtract(1, 'month')
          .endOf('month')
        break
      default:
        return
    }

    const newDateRange = [startDate, endDate]
    setDateRange(newDateRange)

    // Close the calendar after selecting a preset
    setTimeout(() => {
      setCalendarOpen(false)
    }, 100)
  }

  // Disable future dates
  const disabledDate = current => {
    return current && current > moment().endOf('day')
  }
  // Custom render function for the date picker
  const renderExtraFooter = () => (
    <div className="custom-presets-footer">
      <div className="presets-title">{t('v4:quick_select')}</div>
      <div className="presets-list">
        <Button
          type="link"
          size="small"
          onClick={() => handlePresetChange('today')}
          className={selectedPreset === 'today' ? 'preset-active' : ''}
        >
          {t('reports:today')}
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => handlePresetChange('yesterday')}
          className={selectedPreset === 'yesterday' ? 'preset-active' : ''}
        >
          {t('v4:yesterday')}
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => handlePresetChange('lastWeek')}
          className={selectedPreset === 'lastWeek' ? 'preset-active' : ''}
        >
          {t('v4:last_week')}
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => handlePresetChange('lastMonth')}
          className={selectedPreset === 'lastMonth' ? 'preset-active' : ''}
        >
          {t('v4:Last_month')}
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => handlePresetChange('lastQuarter')}
          className={selectedPreset === 'lastQuarter' ? 'preset-active' : ''}
        >
          {t('v4:last_quarter')}
        </Button>
      </div>
    </div>
  )

  return (
    <div className="leaderboard-main">
      <div className="leaderboard-settings-title-row">
        <div>
          <h2 className="leaderboard-settings-title">
            {t('v4:historical_leaderboard')}
          </h2>
          <div className="leaderboard-settings-desc">
            {t(
              'v4:track_your_performance_and_see_how_you_rank_among_your_peers'
            )}
          </div>
        </div>
      </div>
      {error && (
        <div
          className="error-message"
          style={{
            color: 'red',
            marginBottom: '16px',
            padding: '8px',
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '4px'
          }}
        >
          {error}
        </div>
      )}
      <div className="date-range-section">
        <div className="date-range-container">
          <div className="date-picker-section">
            <label className="date-picker-label">{t('v4:time_period')}</label>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              className="date-range-picker"
              allowClear={false}
              placeholder={['From Date', 'To Date']}
              showTime={false}
              separator=" to "
              disabledDate={disabledDate}
              open={calendarOpen}
              onOpenChange={setCalendarOpen}
              renderExtraFooter={renderExtraFooter}
            />
          </div>
        </div>
      </div>
      <div className="competitions-list-section">
        <div className="leaderboard-table-row">
          <LeaderboardTable
            isHistorical={true}
            historicalData={historicalData}
            loading={fetchingHistorical}
            selectedCompetition={selectedCompetition}
            userId={userId}
          />
          <div className="competitions-pagination-row">
            <span className="pagination-arrow">&lt;</span>
            <span className="pagination-page">1</span>
            <span className="pagination-arrow">&gt;</span>
            <span className="pagination-info">{t('v4:page_of')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoricalLeaderboard
